/**
 * voice-v8 — Nachhören, die Seite. (26.09.2026, Ticket 2672)
 *
 * Martin, 26.09., 17:33 auf der Autobahn: „Senden" löste nicht aus — *„zum
 * hundertsten Mal"*. Das Auslösewort wird in der Live-Mitschrift gesucht,
 * die Stille aus dem Pegel gemessen; beides rechnet die Web-Audio-Verarbeitung
 * der Seite. Steht sie (WebKit nach einem Bluetooth-Profilwechsel, belegt in
 * Ticket 2634), läuft die AUFNAHME weiter, aber Wort und Stille greifen nie —
 * es bleibt nur der Knopf, und den kann er im Auto nicht drücken.
 *
 * Hier zwei Teile:
 *
 *   1. `mitschriftSteht` — DIE Stelle, die entscheidet, dass die Mitschrift
 *      nicht liefert. Aus Signalen, die es schon gibt: kommt am Live-Diktat
 *      noch Ton an, kam es überhaupt zustande, hat die Pegelanzeige ihren
 *      Rechentakt aufgegeben.
 *   2. `starteNachhoeren` — schickt dann die schon aufgenommenen Teile des
 *      Rekorders stückweise an den Server (server/nachhoeren.ts). Der hört
 *      die letzten Sekunden mit derselben Erkennung wie die Sprechtaste und
 *      misst die Stille am Ende aus dem Ton. Das Ergebnis geht an die Karte;
 *      entschieden wird dort mit DERSELBEN Regel wie sonst (jetzt-senden.ts).
 *
 * ⚠️ Nur im Stillstand. Im Normalfall liefert die Mitschrift, und dann geht
 *    keine einzige Anfrage hinaus.
 * ⚠️ Nie zwei Anfragen gleichzeitig: die nächste wird erst geplant, wenn die
 *    vorige zurück ist.
 *
 * 26.09.2026 abends (Nachbesserung):
 *   - Es hört auch nach, wenn Rahmen laufen, aber OHNE_TEXT_AB_MS nach dem
 *     ersten Laut keine Mitschrift kommt — die Verbindung zur Erkennung
 *     hängt (26.09., 17:58: das erste Wort kam nach 28 s).
 *   - Kommt wieder Live-Text, gibt es zurück: keine weiteren Proben, die
 *     Live-Mitschrift entscheidet wieder.
 *   - Eine gescheiterte Probe (`text` null) zählt gar nicht — auch ihre
 *     Stille nicht.
 */

import type { SendeGrund } from "./jetzt-senden.js";

/** So lange darf die Mitschrift nichts bekommen, bevor nachgehört wird. */
export const MITSCHRIFT_STEHT_AB_MS = 2000;
/** Abstand zwischen zwei Proben, von Beginn zu Beginn. */
export const NACHHOER_TAKT_MS = 2500;
/** Mindestens so lange Pause, auch wenn eine Probe länger brauchte. */
const NACHHOER_MIN_PAUSE_MS = 400;

/*
 * ── Der Takt in der Sprechpause (03.10.2026, Jev-Tempo) ────────────────────
 *
 * Martin, 03.10.: *„Jev gibt oft zu langsam das Mikro wieder frei, weil er
 * annimmt, dass noch etwas kommt.“* Gemessen (Journal 01.–03.10., Konto 1, nur
 * Zahlen): auf dem Live-Weg schickt Auto im Mittel 0,1–0,3 s nach dem
 * frühesten Zeitpunkt ab, den die Regel erlaubt — dort wartet die Regel
 * selbst (Jev 0,60–0,79 trennt Satzende und Denkpause nicht). Beim
 * Nachhören kennt die Karte Stille und Text aber nur aus der letzten Probe:
 * bei 2,5 s Takt kam das Absenden ab 02.10. im Mittel ~1,0 s, heute früh bei
 * allen vier Aufnahmen 1,3–1,5 s NACH dem erlaubten Zeitpunkt (Jev 0,92 nach
 * „?“: erlaubt ab 1,2 s, gesendet bei 2,7 s).
 *
 * Darum: endet die letzte Probe mit Text und mindestens NACHHOER_PAUSE_AB_MS
 * Stille, kommt die nächste nach NACHHOER_PAUSE_TAKT_MS. Die Regel selbst
 * (shared/stille-auto.ts) bleibt unberührt — es wird nur früher HINGESEHEN.
 * In der Pause ist eine Probe billig: ohne neuen Ton keine Erkennung, nur der
 * Pegel (server/nachhoeren.ts, `erkannt=still|gleich`).
 *
 * ⚠️ Abschneiden: das Nachhören schickt dann so früh ab wie der Live-Weg —
 *    nicht früher. Die zufällige Verspätung schützte bisher Denkpausen, die
 *    zwischen der erlaubten Zeit und der nächsten Probe endeten.
 * ⚠️ Nur, wenn der Server es mit der Probe sagt (`pauseTakt: true`, nur
 *    Konto 1, Schalter `NACHHOER_PAUSE_TAKT=0`); fehlt die Angabe, gilt der
 *    alte Takt. Der Server weist Proben unter 1 s Abstand ab
 *    (NACHHOER_MIN_ABSTAND_MS) — darum 1,2 s, nicht 1,0 s.
 */
/** Abstand zweier Proben (Beginn zu Beginn) in einer Sprechpause. */
export const NACHHOER_PAUSE_TAKT_MS = 1200;
/** Ab so viel Stille am Ende der letzten Probe gilt sie als Sprechpause. */
export const NACHHOER_PAUSE_AB_MS = 300;

/**
 * Der Abstand bis zur nächsten Probe: der schnelle Takt nur, wenn der Server
 * ihn erlaubt UND die letzte Probe Text mit einer Pause am Ende brachte.
 */
export function nachhoerTaktMs(
  takt: number,
  letzte: { text: string | null; stilleMs: number; pauseTakt: boolean } | null,
): number {
  const pause = letzte !== null && letzte.pauseTakt && !!letzte.text?.trim() && letzte.stilleMs >= NACHHOER_PAUSE_AB_MS;
  return pause ? Math.min(takt, NACHHOER_PAUSE_TAKT_MS) : takt;
}
/**
 * So lange nach dem ersten Laut (Pegel) darf die Mitschrift ohne ein Wort
 * bleiben, obwohl Rahmen ankommen. (26.09.2026 abends)
 */
export const OHNE_TEXT_AB_MS = 4000;

/**
 * Warum nachgehört wird — steht so in der Zeile `[ptt-nachhoeren] start`.
 *
 *   diktat-aus    die Mitschrift kam nicht zustande oder brach ab
 *   diktat-stumm  sie läuft, bekam aber nie Ton (der Rechentakt stand von Anfang an)
 *   diktat-ruht   sie bekam Ton, seit MITSCHRIFT_STEHT_AB_MS nicht mehr
 *   diktat-ohne-text  sie bekommt Ton, liefert aber OHNE_TEXT_AB_MS nach
 *                 dem ersten Laut noch kein Wort (hängende Erkennung)
 *   pegel-tot     die Pegelanzeige hat ihren Rechentakt aufgegeben
 */
type NachhoerGrund = "diktat-aus" | "diktat-stumm" | "diktat-ruht" | "diktat-ohne-text" | "pegel-tot";

interface MitschriftLage {
  /** Wie lange die Web-Aufnahme schon läuft. */
  aufnahmeMs: number;
  /** Die Mitschrift kam nicht zustande oder meldete einen Fehler. */
  diktatAus: boolean;
  /** Seit dem letzten Rahmen am Live-Diktat; `null` = noch nie einer. */
  diktatRuhtMs: number | null;
  /** Die Rechentakt-Leiter der Pegelanzeige meldete „tot". */
  pegelTot: boolean;
  /** Seit dem ersten Laut laut Pegel; `null` = noch keiner. */
  sprichtSeitMs?: number | null;
  /**
   * Wie oft die Live-Mitschrift in dieser Aufnahme schon neuen Text brachte
   * (0 = noch nie). Steigt er während des Nachhörens, liefert sie wieder.
   */
  textStand?: number;
}

/**
 * Liefert die Mitschrift nicht? Dann der Grund, sonst `null`.
 *
 * ⚠️ Erst nach MITSCHRIFT_STEHT_AB_MS Aufnahme: die Mitschrift braucht
 *    ihren Anlauf (Schlüssel, Verbindung), und bis dahin ist nichts verpasst.
 */
export function mitschriftSteht(z: MitschriftLage): NachhoerGrund | null {
  if (z.aufnahmeMs < MITSCHRIFT_STEHT_AB_MS) return null;
  if (z.diktatAus) return "diktat-aus";
  if (z.diktatRuhtMs === null) return "diktat-stumm";
  if (z.diktatRuhtMs >= MITSCHRIFT_STEHT_AB_MS) return "diktat-ruht";
  if ((z.textStand ?? 0) === 0 && (z.sprichtSeitMs ?? 0) >= OHNE_TEXT_AB_MS) return "diktat-ohne-text";
  if (z.pegelTot) return "pegel-tot";
  return null;
}

/** Was eine Probe zurückbringt. `text` null = diesmal nichts erkannt. */
interface NachhoerProbe {
  text: string | null;
  stilleMs: number;
  gesprochen: boolean;
  /**
   * Lag vor dem erkannten Endstück (den letzten 10 s) schon Ton? Dann ist
   * der Text nie „das ganze Diktat" (26.09.2026 spät, dritte Runde). Ein
   * Server ohne diese Angabe (vor dem Neustart) gilt als „ja" — dann löst
   * nachgehört nur „Senden" aus.
   */
  tonDavor: boolean;
}

interface NachhoerTeile {
  nr: number;
  mime: string;
  teile: readonly Blob[];
}

interface NachhoerDeps {
  grund: NachhoerGrund;
  /** Läuft die Seite in der App-Hülle? Nur fürs Protokoll (weg=web nativ=ja). */
  nativ: boolean;
  /** Die Teile des Rekorders bis jetzt — null, wenn es keine Web-Aufnahme gibt. */
  teile: () => NachhoerTeile | null;
  /** Läuft die Aufnahme noch? Sonst hört das Nachhören von selbst auf. */
  laeuft: () => boolean;
  onProbe: (p: NachhoerProbe) => void;
  /**
   * 08.10.2026: der Pegel der Aufnahme, wie der Server ihn bei dieser Probe gemessen hat — dBFS je 100-ms-Fenster,
   * nur die seit der vorigen Probe neuen. Für JEDE gelungene Probe, auch ohne Text: er ist Messung, kein Urteil.
   * Nur für die Anzeige (mic-visualizer.ts, `nachgereicht`).
   */
  onPegel?: (db: readonly number[]) => void;
  holen?: (url: string, init: RequestInit) => Promise<Response>;
  taktMs?: number;
  jetzt?: () => number;
  kennung?: string;
  /** Welle 0 (shared/jev-messung.ts): die Kennung der Aufnahme — nur für die Journalzeile des Servers. */
  az?: string;
}

interface Nachhoerer {
  /** Die Karte hat mit dem Nachgehörten abgeschickt — für die Zeile beim Ende. */
  ausgeloest(grund: SendeGrund): void;
  /** Aufhören: laufende Anfrage abbrechen, Datei am Server löschen lassen. */
  stop(): void;
  /** Wie viele Proben schon zurückkamen (gescheiterte zählen nicht). */
  proben(): number;
  /** Läuft es noch — oder hat es aufgehört (von selbst oder per stop)? */
  laeuft(): boolean;
}

function neueKennung(): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch {
    /* unten weiter */
  }
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
}

/** Höchstens so viele Fenster nimmt die Seite je Probe an (der Server schickt höchstens NACHHOER_PEGEL_MAX). */
const PEGEL_HOECHSTENS = 100;

/**
 * Die Fensterpegel aus der Antwort (`pegel: { ab, db }`, server/nachhoeren.ts) — nur endliche Zahlen zwischen −120
 * und 0 dBFS, höchstens PEGEL_HOECHSTENS. Alles andere (alter Server ohne Feld, kaputte Form): leer.
 */
export function nachhoerPegel(roh: unknown): number[] {
  const db = (roh as { db?: unknown } | null | undefined)?.db;
  if (!Array.isArray(db)) return [];
  return db
    .slice(-PEGEL_HOECHSTENS)
    .filter((x): x is number => typeof x === "number" && Number.isFinite(x))
    .map((x) => Math.max(-120, Math.min(0, x)));
}

export function starteNachhoeren(d: NachhoerDeps): Nachhoerer {
  const holen = d.holen ?? ((url: string, init: RequestInit) => fetch(url, init));
  const takt = d.taktMs ?? NACHHOER_TAKT_MS;
  const jetzt = d.jetzt ?? (() => performance.now());
  const kennung = d.kennung ?? neueKennung();
  let aktiv = true;
  /** Welcher Rekorder, bis zu welchem Teil, bis zu welchem Byte gesendet. */
  let nr = -1;
  let bisTeil = 0;
  let ab = 0;
  let neu = true;
  /** Hat der Server je eine Anfrage bekommen? Dann gehört sich eine Abmeldung. */
  let angemeldet = false;
  let n = 0;
  let grundGesendet: SendeGrund | null = null;
  let laufend: AbortController | null = null;
  let uhr: ReturnType<typeof setTimeout> | null = null;
  /** Was die letzte Probe für den Takt sagt — `null`: keine brauchbare (alter Takt). */
  let letzte: { text: string | null; stilleMs: number; pauseTakt: boolean } | null = null;

  const stop = (): void => {
    if (!aktiv) return;
    aktiv = false;
    if (uhr) clearTimeout(uhr);
    uhr = null;
    laufend?.abort();
    laufend = null;
    if (!angemeldet) return;
    const q = new URLSearchParams({ aufnahme: kennung });
    if (d.az) q.set("az", d.az);
    if (grundGesendet) q.set("ausgeloest", grundGesendet);
    void holen(`/api/voice/nachhoeren/ende?${q.toString()}`, {
      method: "POST",
      credentials: "same-origin",
      keepalive: true,
    }).catch(() => undefined);
  };

  const probe = async (): Promise<void> => {
    uhr = null;
    if (!aktiv) return;
    const t = d.laeuft() ? d.teile() : null;
    if (!t) {
      stop();
      return;
    }
    const beginn = jetzt();
    // Ein neuer Rekorder (Leitungswechsel) oder der Server kennt die Datei
    // nicht mehr: von vorn, mit allen Teilen — der Kopf steckt im ersten.
    if (t.nr !== nr) {
      nr = t.nr;
      bisTeil = 0;
      ab = 0;
      neu = true;
    }
    const neue = t.teile.slice(bisTeil);
    const koerper = new Blob(neue, { type: t.mime });
    const q = new URLSearchParams({ aufnahme: kennung, ab: String(ab) });
    if (neu) {
      q.set("neu", "1");
      q.set("grund", d.grund);
      if (d.az) q.set("az", d.az);
      q.set("nativ", d.nativ ? "ja" : "nein");
    }
    const ctrl = new AbortController();
    laufend = ctrl;
    angemeldet = true;
    letzte = null;
    try {
      const r = await holen(`/api/voice/nachhoeren?${q.toString()}`, {
        method: "POST",
        body: koerper,
        headers: { "Content-Type": "application/octet-stream" },
        credentials: "same-origin",
        signal: ctrl.signal,
      });
      if (!aktiv) return;
      if (r.ok) {
        const j = (await r.json().catch(() => ({}))) as {
          ok?: unknown;
          text?: unknown;
          stilleMs?: unknown;
          gesprochen?: unknown;
          tonDavor?: unknown;
          pauseTakt?: unknown;
          pegel?: unknown;
        };
        if (!aktiv) return;
        // Nur eine echte Antwort des Nachhörens schaltet die Karte um — sonst
        // behauptete der Hinweis „ich höre trotzdem mit", ohne dass es stimmt.
        if (j.ok !== true) {
          stop();
          return;
        }
        bisTeil += neue.length;
        ab += koerper.size;
        neu = false;
        // 08.10.2026: der Pegel zuerst — er gilt auch für eine Probe ohne Text (nachhoerPegel prüft die Form).
        const db = nachhoerPegel(j.pegel);
        if (db.length > 0) d.onPegel?.(db);
        /*
         * 26.09.2026 abends: ohne Text (die Erkennung scheiterte, oder es
         * wurde noch nie etwas erkannt) zählt die Probe gar nicht — auch
         * ihre Stille nicht. Sonst träfe eine frische Stille auf einen alten
         * Text, und „senden" von vorhin schickte ab.
         */
        if (typeof j.text === "string") {
          n++;
          // 03.10.2026: nur ein ausdrückliches `pauseTakt: true` des Servers macht die Pause schneller.
          letzte = { text: j.text, stilleMs: Math.max(0, Number(j.stilleMs) || 0), pauseTakt: j.pauseTakt === true };
          d.onProbe({
            text: j.text,
            stilleMs: Math.max(0, Number(j.stilleMs) || 0),
            gesprochen: j.gesprochen === true,
            tonDavor: j.tonDavor !== false,
          });
        }
      } else if (r.status === 409 || r.status === 410) {
        nr = -1;
      } else if ([401, 403, 404, 413, 415, 507].includes(r.status)) {
        // Nicht angemeldet, fremd, zu groß, kein WebM/MP4 (415), keine
        // sichere Ablage — oder der Server kennt den Weg (noch) nicht: ein
        // weiterer Versuch ändert nichts.
        stop();
        return;
      }
      // 429, 503, 5xx: beim nächsten Takt dasselbe Stück noch einmal.
    } catch {
      /* Netz weg oder abgebrochen — beim nächsten Takt noch einmal. */
    } finally {
      if (laufend === ctrl) laufend = null;
    }
    if (!aktiv) return;
    const warte = Math.max(NACHHOER_MIN_PAUSE_MS, nachhoerTaktMs(takt, letzte) - (jetzt() - beginn));
    uhr = setTimeout(() => void probe(), warte);
  };

  void probe();

  return {
    ausgeloest(grund) {
      // Nur, wenn die Karte wirklich mit Nachgehörtem entschied.
      if (n > 0) grundGesendet = grund;
    },
    stop,
    proben: () => n,
    laeuft: () => aktiv,
  };
}

/** So oft fragt die Wache nach. */
const WACHE_PRUEF_MS = 500;

export interface WacheDeps extends Omit<NachhoerDeps, "grund"> {
  /**
   * Die Lage der Mitschrift jetzt — oder `null`, wenn es nichts zu wachen
   * gibt (keine laufende Web-Aufnahme, Wort und Stille beide aus).
   */
  lage: () => MitschriftLage | null;
  pruefMs?: number;
  /**
   * Die Live-Mitschrift liefert wieder, das Nachhören ist zu Ende
   * (26.09.2026 abends). `proben`: so viele kamen bis dahin.
   */
  onZurueck?: (proben: number) => void;
}

export interface MitschriftWache {
  /** Weitergereicht an den Nachhörer, falls er läuft. */
  ausgeloest(grund: SendeGrund): void;
  stop(): void;
  /** Läuft das Nachhören schon? */
  hoertNach(): boolean;
}

/**
 * Die Wache über eine Aufnahme: fragt alle WACHE_PRUEF_MS `mitschriftSteht`
 * und startet beim ersten Ja genau EINEN Nachhörer. Bis dahin geht keine
 * Anfrage hinaus.
 *
 * 26.09.2026 abends: sie wacht weiter, während nachgehört wird. Bringt die
 * Live-Mitschrift neuen Text UND steht sie nach derselben Regel nicht mehr,
 * endet das Nachhören (`onZurueck`), und die Wache wacht von vorn. Hört
 * der Nachhörer von selbst auf (404, fremd, zu groß), bleibt es dabei —
 * ein neuer Anlauf änderte nichts.
 */
export function wacheUeberMitschrift(d: WacheDeps): MitschriftWache {
  let nachhoerer: Nachhoerer | null = null;
  let textStandBeiStart = 0;
  let uhr: ReturnType<typeof setInterval> | null = null;
  const halt = (): void => {
    if (uhr) clearInterval(uhr);
    uhr = null;
  };
  uhr = setInterval(() => {
    const lage = d.lage();
    if (!lage) return;
    if (nachhoerer) {
      if (!nachhoerer.laeuft()) {
        halt();
        return;
      }
      if ((lage.textStand ?? 0) > textStandBeiStart && mitschriftSteht(lage) === null) {
        const n = nachhoerer.proben();
        nachhoerer.stop();
        nachhoerer = null;
        d.onZurueck?.(n);
      }
      return;
    }
    const grund = mitschriftSteht(lage);
    if (!grund) return;
    textStandBeiStart = lage.textStand ?? 0;
    nachhoerer = starteNachhoeren({ ...d, grund });
  }, d.pruefMs ?? WACHE_PRUEF_MS);
  return {
    ausgeloest: (grund) => nachhoerer?.ausgeloest(grund),
    stop() {
      halt();
      nachhoerer?.stop();
    },
    hoertNach: () => nachhoerer !== null,
  };
}
